import { createFileRoute, useNavigate, Link } from '@tanstack/react-router';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { usePlatformAdmin } from '../../../hooks';
import {
  MACHINE_SPECS,
  REGION_PRICING,
  REGION_LABELS,
  computePrice,
  relativePerf,
  HOURS_PER_MONTH,
  type MachineSpec,
  type MachineFamily,
  type PriceBreakdown,
} from '../../../lib/gcpPricing';
import adminStyles from '../Admin.module.css';
import styles from './Pricing.module.css';

export const Route = createFileRoute('/admin/pools/pricing')({
  component: PoolPricingRoute,
});

type ViewRegion = string;

const FAMILY_LABEL: Record<MachineFamily, string> = {
  g2: 'G2 — NVIDIA L4 (Ada)',
  g4: 'G4 — NVIDIA RTX PRO 6000 (Blackwell)',
  n1: 'N1 + T4 vWS (Turing)',
};

const GPU_DESC: Record<string, string> = {
  l4: 'L4',
  'rtx-pro-6000': 'RTX-Pro 6000',
  t4: 'T4',
};

// Columns per family, derived from REGION_PRICING rather than hand-listed:
// a region has a family's rates only where that family is actually offered
// AND priced (see the notes in gcpPricing.ts), so this is exactly the set of
// cells that can render a number. The previous hand-maintained lists had
// drifted — they still omitted us-west4 and asia-east1, both of which now
// carry G4 and are two of the five regions with an 8-GPU RTX grant.
const FAMILY_RATE_KEY: Record<MachineFamily, keyof (typeof REGION_PRICING)[string]> = {
  g2: 'g2Core',
  g4: 'g4Core',
  n1: 'n1Core',
};

const FAMILY_REGION_GROUPS: Record<MachineFamily, ViewRegion[]> = {
  g2: [], g4: [], n1: [],
};
for (const family of Object.keys(FAMILY_REGION_GROUPS) as MachineFamily[]) {
  FAMILY_REGION_GROUPS[family] = Object.keys(REGION_PRICING).filter(
    r => REGION_PRICING[r][FAMILY_RATE_KEY[family]] != null,
  );
}

function fmtUsd(n: number, digits = 3): string {
  return `$${n.toFixed(digits)}`;
}

function fmtPerf(n: number): string {
  return n.toFixed(1) + '×';
}

function PoolPricingRoute() {
  const { isPlatformAdmin } = usePlatformAdmin();
  const navigate = useNavigate();

  const [withWindows, setWithWindows] = useState(true);
  const [withVws, setWithVws] = useState(true);
  const [bootDiskGb, setBootDiskGb] = useState(100);
  const [rankRegion, setRankRegion] = useState<ViewRegion>('us-central1');

  useEffect(() => {
    if (isPlatformAdmin === false) navigate({ to: '/' });
  }, [isPlatformAdmin, navigate]);

  // Rank all shapes priceable in the selected region by perf-per-dollar
  // (perf relative to L4 baseline / $/hr at current Windows + boot-disk
  // inputs). Higher = better value.
  const ranked = useMemo(() => {
    const rows: Array<{
      spec: MachineSpec;
      price: PriceBreakdown;
      perf: number;
      perfPerDollar: number;
    }> = [];
    for (const spec of MACHINE_SPECS) {
      const price = computePrice(spec, rankRegion, { withWindows, withVws, bootDiskGb });
      if (!price) continue;
      const perf = relativePerf(spec);
      rows.push({ spec, price, perf, perfPerDollar: perf / price.total });
    }
    rows.sort((a, b) => b.perfPerDollar - a.perfPerDollar);
    return rows;
  }, [rankRegion, withWindows, withVws, bootDiskGb]);

  if (!isPlatformAdmin) return null;

  return (
    <div className={adminStyles.container}>
      <header className={adminStyles.header}>
        <div>
          <h1 className={adminStyles.title}>Pool Pricing Reference</h1>
          <div className={styles.subtitle}>
            <span>GCP on-demand list prices</span>
            <Link to="/admin/pools" className={styles.crumbLink}>Back to Pools</Link>
          </div>
        </div>
      </header>

      <section className={styles.controlsBar}>
        <label className={styles.control}>
          <span className={styles.controlLabel}>OS</span>
          <select
            className={styles.controlInput}
            value={withWindows ? 'win' : 'lin'}
            onChange={e => setWithWindows(e.target.value === 'win')}
          >
            <option value="win">Windows Server 2025</option>
            <option value="lin">Linux</option>
          </select>
        </label>
        <label className={styles.control}>
          <span className={styles.controlLabel}>GPU SKU</span>
          <select
            className={styles.controlInput}
            value={withVws ? 'vws' : 'standard'}
            onChange={e => setWithVws(e.target.value === 'vws')}
          >
            <option value="vws">vWS (+$0.20/GPU/hr)</option>
            <option value="standard">Standard (bare)</option>
          </select>
        </label>
        <label className={styles.control}>
          <span className={styles.controlLabel}>Boot disk (pd-ssd)</span>
          <select
            className={styles.controlInput}
            value={bootDiskGb}
            onChange={e => setBootDiskGb(parseInt(e.target.value, 10))}
          >
            <option value={50}>50 GB</option>
            <option value={100}>100 GB</option>
            <option value={200}>200 GB</option>
            <option value={500}>500 GB</option>
          </select>
        </label>
        <label className={styles.control}>
          <span className={styles.controlLabel}>Rank region</span>
          <select
            className={styles.controlInput}
            value={rankRegion}
            onChange={e => setRankRegion(e.target.value)}
          >
            {Object.keys(REGION_PRICING).map(r => (
              <option key={r} value={r}>{REGION_LABELS[r] ?? r} · {r}</option>
            ))}
          </select>
        </label>
        <div className={styles.controlNote}>
          List prices · SUDs not applied · pulled from Cloud Billing 2026-09-02
        </div>
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>
          Best perf/$ — {withWindows ? 'Windows' : 'Linux'} single-session
        </h2>
        <p className={styles.sectionLead}>
          Shapes ranked by relative graphics-and-encode perf (L4 = 1.0×)
          per hourly dollar in <strong>{REGION_LABELS[rankRegion] ?? rankRegion}</strong>.
          Top of the list is the cheapest way to get a given amount of GPU work done.
        </p>

        <div className={styles.tableScroll}>
          <table className={styles.priceTable}>
            <thead>
              <tr>
                <th className={styles.colRank}>#</th>
                <th className={styles.colShape}>Shape</th>
                <th>GPU</th>
                <th className={styles.numCol}>vCPU</th>
                <th className={styles.numCol}>RAM</th>
                <th className={styles.numCol}>VRAM</th>
                <th className={styles.numCol}>$/hr</th>
                <th className={styles.numCol}>$/mo</th>
                <th className={styles.numCol}>Rel. perf</th>
                <th className={styles.numCol}>perf/$ (rel/hr)</th>
              </tr>
            </thead>
            <tbody>
              {ranked.map((row, i) => {
                const isWinner = i === 0;
                const gpuDesc = `${row.spec.gpu.count}× ${GPU_DESC[row.spec.gpu.kind]}` +
                  (row.spec.gpu.fraction ? ` (1/${Math.round(1 / row.spec.gpu.fraction)} slice)` : '');
                return (
                  <tr key={row.spec.name} className={isWinner ? styles.winnerRow : undefined}>
                    <td className={`${styles.colRank} ${isWinner ? styles.winnerRank : ''}`}>
                      {i + 1}
                    </td>
                    <td className={styles.colShape}>
                      <span className={styles.shapeName}>{row.spec.name}</span>
                    </td>
                    <td>{gpuDesc}</td>
                    <td className={styles.numCol}>{row.spec.vcpus}</td>
                    <td className={styles.numCol}>{row.spec.ramGb} GB</td>
                    <td className={styles.numCol}>{row.spec.gpu.vramGb} GB</td>
                    <td className={styles.numCol}>{fmtUsd(row.price.total)}</td>
                    <td className={styles.numCol}>
                      ${(row.price.total * HOURS_PER_MONTH).toFixed(0)}
                    </td>
                    <td className={styles.numCol}>{fmtPerf(row.perf)}</td>
                    <td className={`${styles.numCol} ${styles.strong}`}>
                      {row.perfPerDollar.toFixed(2)}
                    </td>
                  </tr>
                );
              })}
              {ranked.length === 0 && (
                <tr>
                  <td colSpan={10} className={adminStyles.empty}>
                    No shapes priced in this region
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <FamilyBreakdown
        family="g2"
        withWindows={withWindows}
        withVws={withVws}
        bootDiskGb={bootDiskGb}
      />
      <FamilyBreakdown
        family="g4"
        withWindows={withWindows}
        withVws={withVws}
        bootDiskGb={bootDiskGb}
      />
      <FamilyBreakdown
        family="n1"
        withWindows={withWindows}
        withVws={withVws}
        bootDiskGb={bootDiskGb}
      />

      <section className={styles.notes}>
        <h3 className={styles.notesTitle}>Notes &amp; caveats</h3>
        <ul className={styles.notesList}>
          <li>
            <strong>List prices only.</strong> Sustained-use discounts (SUD) auto-apply at
            billing — typically 10–30% off for instances running &gt; 25% of a month. Actual
            spend is lower than the figures here.
          </li>
          <li>
            <strong>Windows licensing</strong> is $0.046 / vCPU / hour on Server 2025 Datacenter.
            On a g2-standard-12 that's $0.55/hr — about a third of the bill.
          </li>
          <li>
            <strong>G4 MIG-slice billing.</strong> Fractional shapes (g4-standard-6/12/24)
            are assumed to be charged at the slice fraction of the per-GPU SKU. The Cloud
            Billing catalog doesn't expose a per-slice SKU, so verify against the GCP
            pricing calculator before committing.
          </li>
          <li>
            <strong>Data volume.</strong> The single-card G2 sizes carry a 375 GiB
            Hyperdisk Balanced at 1200 MBps instead of Local SSD — 3.1× the write
            throughput, and the cache survives a reboot. It bills as capacity plus only
            the IOPS and MBps above the included 3,000 / 140, so $81.40/mo in Iowa,
            about $0.28 more than the Local SSD across a 4-hour node. Multi-card G2 and
            all of G4 keep Local SSD: they stripe several disks and already clear 1200
            MBps, and on G4 those disks are bundled into the shape at $0. N1 keeps Local
            SSD too, pending confirmation that N1 can attach a Hyperdisk at all.
          </li>
          <li>
            <strong>Region coverage.</strong> A cell shows "—" when the region either has
            no such hardware or GCP hasn't published the SKU. At this snapshot: no G4 in
            europe-west6 (Zurich), europe-west3 (Frankfurt), us-west2 (LA) or
            australia-southeast1 (Sydney); southamerica-east1 (São Paulo) has G4 machines
            but no published G4 price yet. Sydney and LA have no L4 either — T4 only.
          </li>
          <li>
            <strong>vWS vs standard is $0.20/GPU/hr</strong> — one flat global licence SKU,
            same in every region, on top of an unchanged GPU rate. The vWS part is what
            gives WDDM, which DirectX and NVENC need. Our G4 and N1+T4 pools run vWS;
            our G2 pools run bare L4, which is why the toggle has no single right default.
          </li>
          <li>
            <strong>A price here is not permission to launch.</strong> The two SKUs draw on
            separate regional quota, so the toggle is a capacity question as much as a cost
            one. RTX PRO 6000 vWS as of 2026-09-02: 8 in us-west4, us-east1, us-east4,
            asia-southeast1 and asia-east1, 4 in europe-west1 and europe-west2, and{' '}
            <em>zero</em> in us-west1, us-central1, europe-west4 and asia-northeast1 — all
            four of which price out fine here and would still be refused. us-west1 holds 8
            of the bare part, so it is reachable on standard and not on vWS. L4 goes the
            other way: the bare grant is 64 in our biggest regions against a 32 default for
            L4 vWS, so moving a large G2 pool to vWS lowers its ceiling.
          </li>
          <li>
            <strong>Relative perf</strong> uses L4 = 1.0× as the baseline, T4 ≈ 0.33×,
            RTX PRO 6000 (full) ≈ 3.5×. Coarse ratio for ranking only — measure your
            actual UE workload before sizing.
          </li>
        </ul>
      </section>
    </div>
  );
}

function FamilyBreakdown({
  family,
  withWindows,
  withVws,
  bootDiskGb,
}: {
  family: MachineFamily;
  withWindows: boolean;
  withVws: boolean;
  bootDiskGb: number;
}) {
  const specs = useMemo(() => MACHINE_SPECS.filter(s => s.family === family), [family]);
  const regions = FAMILY_REGION_GROUPS[family];
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const toggleRow = (name: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  return (
    <section className={styles.section}>
      <h2 className={styles.sectionTitle}>{FAMILY_LABEL[family]}</h2>
      <p className={styles.sectionLead}>
        Per-region $/hr · {withWindows ? 'Windows' : 'Linux'} · {withVws ? 'vWS' : 'standard'} GPU
        {' '}· {bootDiskGb} GB boot pd-ssd
        {family === 'g4'
          ? ' (Local SSD bundled)'
          : family === 'g2'
            ? ' + 375 GB Hyperdisk on the 1-GPU sizes, striped Local SSD on the rest'
            : ' + 375 GB Local SSD'}.
        Click a row to see the line-item breakdown.
      </p>

      <div className={styles.tableScroll}>
        <table className={styles.priceTable}>
          <thead>
            <tr>
              <th className={styles.colShape}>Shape</th>
              <th>GPU</th>
              <th className={styles.numCol}>vCPU/RAM</th>
              <th className={styles.numCol}>VRAM</th>
              {regions.map(r => (
                <th key={r} className={styles.numCol} title={REGION_LABELS[r] ?? r}>
                  {r}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {specs.map(spec => {
              const isExpanded = expanded.has(spec.name);
              const cells = regions.map(r => ({
                region: r,
                price: computePrice(spec, r, { withWindows, withVws, bootDiskGb }),
              }));
              const gpuDesc = `${spec.gpu.count}× ${GPU_DESC[spec.gpu.kind]}` +
                (spec.gpu.fraction ? ` (1/${Math.round(1 / spec.gpu.fraction)})` : '');
              return (
                <Fragment key={spec.name}>
                  <tr
                    className={`${styles.clickableRow} ${isExpanded ? styles.expandedRow : ''}`}
                    onClick={() => toggleRow(spec.name)}
                  >
                    <td className={styles.colShape}>
                      <span className={styles.toggleCaret}>{isExpanded ? '▾' : '▸'}</span>
                      <span className={styles.shapeName}>{spec.name}</span>
                    </td>
                    <td>{gpuDesc}</td>
                    <td className={styles.numCol}>{spec.vcpus} / {spec.ramGb} GB</td>
                    <td className={styles.numCol}>{spec.gpu.vramGb} GB</td>
                    {cells.map(({ region, price }) => (
                      <td key={region} className={styles.numCol}>
                        {price ? fmtUsd(price.total) : <span className={styles.dim}>—</span>}
                      </td>
                    ))}
                  </tr>
                  {isExpanded && (
                    <tr className={styles.detailRow}>
                      <td colSpan={4 + regions.length}>
                        <BreakdownGrid
                          spec={spec}
                          regions={regions}
                          withWindows={withWindows}
                          withVws={withVws}
                          bootDiskGb={bootDiskGb}
                        />
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function BreakdownGrid({
  spec,
  regions,
  withWindows,
  withVws,
  bootDiskGb,
}: {
  spec: MachineSpec;
  regions: string[];
  withWindows: boolean;
  withVws: boolean;
  bootDiskGb: number;
}) {
  return (
    <div className={styles.breakdownGrid}>
      {regions.map(r => {
        const price = computePrice(spec, r, { withWindows, withVws, bootDiskGb });
        if (!price) {
          return (
            <div key={r} className={styles.breakdownCard}>
              <div className={styles.breakdownCardHeader}>
                {REGION_LABELS[r] ?? r} <span className={styles.dim}>· {r}</span>
              </div>
              <div className={styles.dim}>Not priced in this region</div>
            </div>
          );
        }
        const monthly = price.total * HOURS_PER_MONTH;
        return (
          <div key={r} className={styles.breakdownCard}>
            <div className={styles.breakdownCardHeader}>
              {REGION_LABELS[r] ?? r} <span className={styles.dim}>· {r}</span>
            </div>
            <dl className={styles.breakdownList}>
              <dt>Compute (vCPU + RAM)</dt>
              <dd>{fmtUsd(price.compute, 4)}</dd>
              <dt>GPU</dt>
              <dd>{fmtUsd(price.gpu, 4)}</dd>
              {price.vws > 0 && (
                <>
                  <dt>vWS license</dt>
                  <dd>{fmtUsd(price.vws, 4)}</dd>
                </>
              )}
              {price.windows > 0 && (
                <>
                  <dt>Windows license</dt>
                  <dd>{fmtUsd(price.windows, 4)}</dd>
                </>
              )}
              <dt>Disks (boot + local SSD)</dt>
              <dd>{fmtUsd(price.disks, 4)}</dd>
              <dt className={styles.totalDt}>Total / hr</dt>
              <dd className={styles.totalDd}>{fmtUsd(price.total)}</dd>
              <dt className={styles.totalDt}>Total / mo</dt>
              <dd className={styles.totalDd}>${monthly.toFixed(0)}</dd>
            </dl>
          </div>
        );
      })}
    </div>
  );
}
